import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api/client";
import Dashboard from "../components/Dashboard";
import { takePendingAnalysisFile } from "../fileTransfer";

// Module-level session cache — persists across route navigations within the same auth session
let _cachedSessionId = null;

export default function DrawingEvaluationPage({ sessionId, onLogout }) {
  const location = useLocation();
  const [localSessionId, setLocalSessionId] = useState(sessionId || _cachedSessionId);
  const [isCreating, setIsCreating] = useState(!localSessionId);

  // Read any pending file + initial mode set by ExploreFilesPage
  const [initialMode]         = useState(() => location.state?.initialMode || "evaluate");
  const [initialAnalysisFile] = useState(() => takePendingAnalysisFile());
  const [initialCompareId]    = useState(() => location.state?.initialCompareId || null);

  useEffect(() => {
    if (localSessionId) {
      _cachedSessionId = localSessionId;
      return;
    }
    let cancelled = false;
    setIsCreating(true);
    axios
      .post(`${API}/sessions/create`, {})
      .then((res) => {
        if (!cancelled) {
          _cachedSessionId = res.data.session_id;
          setLocalSessionId(res.data.session_id);
          setIsCreating(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsCreating(false);
          toast.error("Failed to create evaluation session. Please refresh.");
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Sync if parent provides a new sessionId
  useEffect(() => {
    if (sessionId && sessionId !== localSessionId) {
      _cachedSessionId = sessionId;
      setLocalSessionId(sessionId);
      setIsCreating(false);
    }
  }, [sessionId]);

  if (isCreating || !localSessionId) {
    return (
      <div
        className="flex-1 flex items-center justify-center bg-slate-50"
        data-testid="drawing-evaluation-loading"
      >
        <div className="text-center space-y-3">
          <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-slate-500">Preparing evaluation session…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="drawing-evaluation-page">
      <Dashboard
        sessionId={localSessionId}
        onLogout={onLogout}
        initialMode={initialMode}
        initialAnalysisFile={initialAnalysisFile}
        initialCompareId={initialCompareId}
      />
    </div>
  );
}
